import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  LayoutList,
} from 'lucide-react'
import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  isBefore,
  startOfDay,
  addDays,
  addWeeks,
  addMonths,
} from 'date-fns'

import { PageShell } from '@/components/common/PageShell'
import {
  WorkspaceToolbar,
  WorkspaceToolbarSearch,
} from '@/components/common/WorkspaceToolbar'
import { Button, Card, Modal, Tabs, TabsList, TabsTrigger } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { useMyCompanyFilter } from '@/features/companies/hooks/useMyCompanyFilter'
import { useConfirmShiftAssignment } from '../hooks/useShifts'
import {
  useShiftAssignmentsAcrossCompanies,
  type CompanyScopedAssignment,
} from '../hooks/useShiftsAcrossCompanies'
import { getAssignmentShift, getAssignmentShiftName } from '../utils/assignmentUtils'
import { getCompanyColor } from '../utils/companyColor'
import { type ShiftViewMode } from '../utils/dateRange'
import { CompanyChips } from './CompanyChips'
import { MyShiftsCalendar } from './MyShiftsCalendar'
import type { ShiftAssignment } from '@/types/shift.types'
import { cn } from '@/lib/cn'
import { QueryErrorState } from '@/components/common/QueryErrorState'

type Filter = 'upcoming' | 'past' | 'all'
type ViewLayout = 'list' | 'calendar'
type ShiftPopupMode = 'confirm' | 'view'

const STATUS_STYLES: Record<ShiftAssignment['status'], string> = {
  scheduled:
    'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800',
  confirmed:
    'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800',
  cancelled:
    'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
}

function getDateRangeForFilter(filter: Filter): {
  startDate?: string
  endDate?: string
} {
  const today = format(startOfDay(new Date()), 'yyyy-MM-dd')
  if (filter === 'upcoming') return { startDate: today }
  if (filter === 'past') return { endDate: today }
  return {}
}

function groupByWeek(
  assignments: CompanyScopedAssignment[]
): Map<string, CompanyScopedAssignment[]> {
  const out = new Map<string, CompanyScopedAssignment[]>()
  const sorted = [...assignments].sort((a, b) => {
    const dateCompare = b.date.localeCompare(a.date)
    if (dateCompare !== 0) return dateCompare
    return (b.startTime ?? '').localeCompare(a.startTime ?? '')
  })
  for (const a of sorted) {
    const start = startOfWeek(parseISO(a.date), { weekStartsOn: 1 })
    const end = endOfWeek(parseISO(a.date), { weekStartsOn: 1 })
    const key = `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`
    if (!out.has(key)) out.set(key, [])
    out.get(key)!.push(a)
  }
  return out
}

function ShiftActionPreviewModal({
  mode,
  assignment,
  companyName,
  loading,
  onClose,
  onConfirm,
}: {
  mode: ShiftPopupMode
  assignment: CompanyScopedAssignment | null
  companyName: string
  loading: boolean
  onClose: () => void
  onConfirm: () => void
}): React.ReactElement {
  const shift = assignment ? getAssignmentShift(assignment, new Map()) : null
  const startTime = shift && 'startTime' in shift ? shift.startTime : ''
  const endTime = shift && 'endTime' in shift ? shift.endTime : ''
  const isConfirmMode = mode === 'confirm'

  return (
    <Modal
      open={!!assignment}
      onClose={onClose}
      title={isConfirmMode ? 'Confirm shift' : 'Shift details'}
      className="max-w-sm"
      footer={
        isConfirmMode ? (
          <>
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={onConfirm} loading={loading}>
              Confirm
            </Button>
          </>
        ) : (
          <Button type="button" variant="primary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {assignment ? (
        <div className="space-y-4">
          {isConfirmMode ? (
            <p className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-200">
              Review this shift before confirming it.
            </p>
          ) : null}
          <dl className="grid gap-3 text-sm">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Company</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {companyName || 'Company'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Shift</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {getAssignmentShiftName(assignment)}
            </dd>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Date</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">
                {format(parseISO(assignment.date), 'EEE, d MMM')}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Time</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">
                {startTime && endTime ? `${startTime} - ${endTime}` : '-'}
              </dd>
            </div>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Status</dt>
            <dd className="font-medium capitalize text-gray-900 dark:text-gray-100">
              {assignment.status}
            </dd>
          </div>
          {assignment.notes ? (
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Notes</dt>
              <dd className="text-gray-900 dark:text-gray-100">{assignment.notes}</dd>
            </div>
          ) : null}
          </dl>
        </div>
      ) : null}
    </Modal>
  )
}

export function MyShiftsPage(): React.ReactElement {
  const companyFilter = useMyCompanyFilter()
  const { companies: myCompanies, companiesLoading, selectedCompanyIds } = companyFilter
  const [searchParams] = useSearchParams()
  // Deep link from a shift notification (?shiftAssignmentId=…): focus the calendar on that
  // shift's date and briefly highlight it.
  const deepLinkAssignmentId =
    searchParams.get('shiftAssignmentId') ?? searchParams.get('assignment') ?? undefined
  const [layout, setLayout] = useState<ViewLayout>('calendar')
  const [filter, setFilter] = useState<Filter>('upcoming')
  const [highlightId, setHighlightId] = useState<string | undefined>(deepLinkAssignmentId)
  // Re-arm the highlight when the deep-link param changes — covers clicking another
  // notification while already on this page (the component doesn't remount). Set-state-during-
  // render; lastSeenDeepLinkId tracks edges so re-clicking the same shift re-arms too.
  const [lastSeenDeepLinkId, setLastSeenDeepLinkId] = useState<string | undefined>(
    deepLinkAssignmentId
  )
  if (deepLinkAssignmentId !== lastSeenDeepLinkId) {
    setLastSeenDeepLinkId(deepLinkAssignmentId)
    if (deepLinkAssignmentId) setHighlightId(deepLinkAssignmentId)
  }
  const [calendarView, setCalendarView] = useState<ShiftViewMode>('monthly')
  const [focusedDate, setFocusedDate] = useState<Date>(() => new Date())
  const [search, setSearch] = useState('')
  const [shiftPopup, setShiftPopup] = useState<{
    mode: ShiftPopupMode
    assignment: CompanyScopedAssignment
  } | null>(null)
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])

  const visibleCompanies = useMemo(
    () =>
      selectedCompanyIds.size === 0
        ? myCompanies
        : myCompanies.filter((c) => selectedCompanyIds.has(c._id)),
    [myCompanies, selectedCompanyIds]
  )
  const visibleCompanyIds = useMemo(() => visibleCompanies.map((c) => c._id), [visibleCompanies])
  const companyNameById = useMemo(() => {
    const m = new Map<string, string>()
    myCompanies.forEach((c) => m.set(c._id, c.name))
    return m
  }, [myCompanies])

  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => clearTimeout(t)
  }, [search])

  const params = useMemo(
    () => ({
      // Calendar mode loads every shift (past → today → future) so navigating
      // months never refetches and all days are populated; the grid itself only
      // displays the focused window. List mode keeps the upcoming/past filter.
      ...(layout === 'calendar' ? {} : getDateRangeForFilter(filter)),
      limit: 100,
      timezone,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [layout, filter, debouncedSearch, timezone]
  )

  const { assignments, isLoading, isError, error, refetch } = useShiftAssignmentsAcrossCompanies(
    visibleCompanyIds,
    params,
    { mineOnly: true }
  )
  const confirmAssignment = useConfirmShiftAssignment()
  const openShiftPopup = (
    assignment: CompanyScopedAssignment,
    mode: ShiftPopupMode
  ): void => {
    setShiftPopup({ mode, assignment })
  }
  const runConfirmPreview = (): void => {
    if (!shiftPopup || shiftPopup.mode !== 'confirm') return
    const assignment = shiftPopup.assignment
    confirmAssignment.mutate(
      { companyId: assignment.companyId, id: assignment._id },
      {
        onSuccess: () =>
          setShiftPopup({
            mode: 'view',
            assignment: {
              ...assignment,
              status: 'confirmed',
              confirmedAt: new Date().toISOString(),
            },
          }),
      }
    )
  }

  // Server already filtered by shift name; we still match company name client-side
  // since the backend's $lookup is on shifts, not companies.
  const filteredAssignments = useMemo(() => {
    if (!debouncedSearch) return assignments
    const q = debouncedSearch.toLowerCase()
    return assignments.filter((a) => {
      const name = getAssignmentShiftName(a).toLowerCase()
      const cName = companyNameById.get(a.companyId)?.toLowerCase() ?? ''
      return name.includes(q) || cName.includes(q)
    })
  }, [assignments, debouncedSearch, companyNameById])

  const grouped = useMemo(() => groupByWeek(filteredAssignments), [filteredAssignments])

  // Jump the calendar to the deep-linked shift's date once it's loaded — set-state-during-render
  // (the recommended alternative to an effect for adjusting state from changed data).
  const highlightTarget = highlightId
    ? assignments.find((a) => a._id === highlightId)
    : undefined
  const [focusedHighlightId, setFocusedHighlightId] = useState<string | null>(null)
  if (highlightTarget && focusedHighlightId !== highlightTarget._id) {
    setFocusedHighlightId(highlightTarget._id)
    setFocusedDate(parseISO(highlightTarget.date))
  }
  // Scroll the highlighted shift into view once it has rendered, then fade it out.
  useEffect(() => {
    if (!highlightId) return
    document
      .getElementById(`shift-assignment-${highlightId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const t = setTimeout(() => setHighlightId(undefined), 4000)
    return () => clearTimeout(t)
  }, [highlightId, filteredAssignments, focusedDate])

  const handlePrev = () => {
    if (calendarView === 'daily') setFocusedDate((d) => addDays(d, -1))
    else if (calendarView === 'weekly') setFocusedDate((d) => addWeeks(d, -1))
    else setFocusedDate((d) => addMonths(d, -1))
  }
  const handleNext = () => {
    if (calendarView === 'daily') setFocusedDate((d) => addDays(d, 1))
    else if (calendarView === 'weekly') setFocusedDate((d) => addWeeks(d, 1))
    else setFocusedDate((d) => addMonths(d, 1))
  }
  const rangeLabel =
    calendarView === 'daily'
      ? format(focusedDate, 'PPP')
      : calendarView === 'weekly'
        ? `${format(startOfWeek(focusedDate, { weekStartsOn: 1 }), 'd MMM')} – ${format(endOfWeek(focusedDate, { weekStartsOn: 1 }), 'd MMM yyyy')}`
        : format(focusedDate, 'MMMM yyyy')

  const breadcrumbItems = [{ label: 'My Workspace', to: '/dashboard' }, { label: 'My Shifts' }]

  if (companiesLoading) {
    return (
      <PageShell
        breadcrumbItems={breadcrumbItems}
        icon={CalendarDays}
        title="My Shifts"
        className="gap-0"
        hideHeader
      >
        <SectionLoader variant="list" label="Loading shifts" />
      </PageShell>
    )
  }

  if (myCompanies.length === 0) {
    return (
      <PageShell
        breadcrumbItems={breadcrumbItems}
        icon={CalendarDays}
        title="My Shifts"
        className="gap-0"
        hideHeader
      >
        <Card>
          <p className="py-8 text-center text-sm text-gray-600 dark:text-gray-400">
            You are not assigned to any company yet.
          </p>
        </Card>
      </PageShell>
    )
  }

  const today = startOfDay(new Date())

  return (
    <PageShell
      breadcrumbItems={breadcrumbItems}
      icon={CalendarDays}
      title="My Shifts"
      description="All your shifts across every company you belong to."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        {/* Row 1: company chips + layout toggle + search */}
        <WorkspaceToolbar
          filter={
            <CompanyChips
              companies={myCompanies}
              selectedCompanyIds={selectedCompanyIds}
              onToggle={companyFilter.toggle}
            />
          }
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <div
              className="inline-flex h-8 items-center rounded-md border border-gray-300 p-0.5 dark:border-gray-600"
              role="group"
              aria-label="View layout"
            >
              <button
                type="button"
                onClick={() => setLayout('list')}
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded px-2.5 text-xs font-medium transition-colors',
                  layout === 'list'
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                )}
              >
                <LayoutList className="h-3.5 w-3.5" />
                List
              </button>
              <button
                type="button"
                onClick={() => setLayout('calendar')}
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded px-2.5 text-xs font-medium transition-colors',
                  layout === 'calendar'
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                )}
              >
                <CalendarDays className="h-3.5 w-3.5" />
                Calendar
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <WorkspaceToolbarSearch
                value={search}
                onChange={setSearch}
                placeholder="Search shifts…"
                ariaLabel="Search shifts"
                active={Boolean(debouncedSearch)}
              />
            </div>
          </div>
        </WorkspaceToolbar>

        {/* Row 2: list filter tabs OR calendar view + date navigator */}
        {layout === 'list' ? (
          <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
            <TabsList className="divide-x divide-gray-200 dark:divide-gray-700">
              <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="past">Past</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        ) : (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Tabs
              value={calendarView}
              onValueChange={(v) => setCalendarView(v as ShiftViewMode)}
            >
              <TabsList className="divide-x divide-gray-200 dark:divide-gray-700">
                <TabsTrigger value="daily">Daily</TabsTrigger>
                <TabsTrigger value="weekly">Weekly</TabsTrigger>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="inline-flex h-8 items-center overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
              <button
                type="button"
                onClick={handlePrev}
                className="flex h-full w-8 items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Previous range"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[180px] border-x border-gray-300 px-3 text-center text-sm font-medium text-gray-700 dark:border-gray-600 dark:text-gray-300">
                {rangeLabel}
              </span>
              <button
                type="button"
                onClick={handleNext}
                className="flex h-full w-8 items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Next range"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <Button
              variant="secondary"
              onClick={() => setFocusedDate(new Date())}
              className="h-8"
            >
              Today
            </Button>
          </div>
        )}

        {isLoading ? (
          <Card>
            <SectionLoader variant="list" label="Loading shifts" />
          </Card>
        ) : isError ? (
          <Card>
            <QueryErrorState
              error={error}
              title="Couldn't load your shifts"
              onRetry={refetch}
              className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
            />
          </Card>
        ) : layout === 'calendar' ? (
          <MyShiftsCalendar
            viewMode={calendarView}
            focusedDate={focusedDate}
            assignments={filteredAssignments}
            companyNameById={companyNameById}
            highlightAssignmentId={highlightId}
            onSelect={(a) => {
              const isPast = isBefore(parseISO(a.date), startOfDay(new Date()))
              openShiftPopup(a, a.status === 'scheduled' && !isPast ? 'confirm' : 'view')
            }}
          />
        ) : filteredAssignments.length === 0 ? (
          <Card>
            <p className="py-12 text-center text-sm text-gray-600 dark:text-gray-400">
              No shifts{' '}
              {filter === 'upcoming' ? 'upcoming' : filter === 'past' ? 'in the past' : ''}.
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {Array.from(grouped.entries()).map(([weekLabel, items]) => (
              <Card key={weekLabel} contentClassName="p-0">
                <div className="border-b border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  {weekLabel}
                </div>
                <ul>
                  {items.map((a) => {
                    const shift = getAssignmentShift(a, new Map())
                    const startTime = shift && 'startTime' in shift ? shift.startTime : ''
                    const endTime = shift && 'endTime' in shift ? shift.endTime : ''
                    const isPast = isBefore(parseISO(a.date), today)
                    const canConfirm = a.status === 'scheduled' && !isPast
                    const cName = companyNameById.get(a.companyId) ?? ''
                    const cColor = getCompanyColor(a.companyId)
                    return (
                      <li
                        key={a._id}
                        className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-gray-700"
                      >
                        <div className="min-w-[110px]">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {format(parseISO(a.date), 'EEE, d MMM')}
                          </div>
                          {startTime && endTime && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {startTime} – {endTime}
                            </div>
                          )}
                        </div>
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: cColor.dot }}
                            aria-hidden
                          />
                          <span className="truncate text-sm text-gray-800 dark:text-gray-100">
                            {getAssignmentShiftName(a)}
                          </span>
                          {cName && (
                            <span
                              className={cn(
                                'shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium',
                                cColor.bg,
                                cColor.text
                              )}
                            >
                              {cName}
                            </span>
                          )}
                          {a.notes && (
                            <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                              · {a.notes}
                            </span>
                          )}
                        </div>
                        <span
                          className={cn(
                            'rounded-full border px-2 py-0.5 text-xs font-medium capitalize',
                            STATUS_STYLES[a.status]
                          )}
                        >
                          {a.status}
                        </span>
                        <Button
                          variant="secondary"
                          onClick={() => openShiftPopup(a, 'view')}
                          className="inline-flex items-center gap-1.5"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                        {canConfirm && (
                          <Button
                            variant="secondary"
                            onClick={() => openShiftPopup(a, 'confirm')}
                            loading={confirmAssignment.isPending}
                            className="inline-flex items-center gap-1.5"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Confirm
                          </Button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </Card>
            ))}
          </div>
        )}
        <ShiftActionPreviewModal
          mode={shiftPopup?.mode ?? 'view'}
          assignment={shiftPopup?.assignment ?? null}
          companyName={
            shiftPopup ? companyNameById.get(shiftPopup.assignment.companyId) ?? '' : ''
          }
          loading={confirmAssignment.isPending}
          onClose={() => setShiftPopup(null)}
          onConfirm={runConfirmPreview}
        />
      </div>
    </PageShell>
  )
}

MyShiftsPage.displayName = 'MyShiftsPage'
